// Data Classification Hub Models
export interface UploadFile {
  id: string;
  name: string;
  size: number;
  type: string;
  status: UploadStatus;
  progress?: number;
  error?: string;
  uploadedAt?: Date;
  file?: File;
}

export interface ClassificationConfig {
  supportedFormats: string[];
  maxFileSize: number;
  maxFiles: number;
  autoClassify: boolean;
  confidenceThreshold: number;
}

export interface UploadSession {
  id: string;
  files: UploadFile[];
  createdAt: Date;
  completedAt?: Date;
  status: SessionStatus;
}

export enum UploadStatus {
  PENDING = "pending",
  UPLOADING = "uploading",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed"
}

export enum SessionStatus {
  ACTIVE = "active",
  COMPLETED = "completed",
  CANCELLED = "cancelled"
}

export interface FeatureInfo {
  icon: string;
  title: string;
  description: string;
  color: string;
}

// Cloud Crawler Models
export interface CloudSource {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  connectionType: "oauth" | "api-key" | "credentials";
}

export interface CloudFile {
  id: string;
  name: string;
  size: string;
  modified: string;
  type: "file";
  path: string;
  mimeType?: string;
}

export interface CloudFolder {
  id: string;
  name: string;
  fileCount: number;
  type: "folder";
  path: string;
  lastModified?: string;
}

export type CloudItem = CloudFile | CloudFolder;

export enum CrawlerStep {
  SOURCE = "source",
  CONNECTING = "connecting",
  BROWSE = "browse",
  IMPORTING = "importing",
  COMPLETED = "completed"
}

export interface CloudImportSession {
  id: string;
  sourceId: string;
  sourceName: string;
  selectedItems: CloudItem[];
  status: CrawlerStep;
  progress: number;
  startedAt: Date;
  completedAt?: Date;
  totalItems: number;
  successCount: number;
  failureCount: number;
}

// Extracted Data Models
export interface ExtractedData {
  text?: string;
  metadata?: Record<string, any>;
  entities?: string[];
  keywords?: string[];
  summary?: string;
}

export type FileData = {
  id: string;
  name: string;
  category: string;
  confidence: number;
  reason: string;
  validated: boolean;
  extracted?: ExtractedData;
  guidCode?: string;
  title?: string;
  fileName?: string;
  predictedCategory?: string;
  summary?: string;
};

// Category Overview Models
export interface CategoryCount {
  id: string;
  name: string;
  count: number;
  color: string;
  description?: string;
  lastUpdated?: Date;
}

export interface ClassificationResult {
  id: string;
  fileName: string;
  category: string;
  confidence: number;
  classifiedAt: Date;
  metadata?: {
    fileSize: number;
    fileType: string;
    extractedText?: string;
    tags?: string[];
  };
}

export interface ClassificationSummary {
  totalDocuments: number;
  totalCategories: number;
  categories: CategoryCount[];
  averageConfidence: number;
  classificationDate: Date;
  processingTime?: number;
}

// Batch Quality Assessment Models
export interface FieldWiseAnalysis {
  validation_type: string;
  status: "pass" | "fail";
  documents_passed: number;
  documents_failed: number;
  issues: string[];
  examples?: {
    values_validated?: Array<{
      doc_id: string;
      field: string;
      value: string;
      validation_tool: string;
      result: "pass" | "fail";
      reason: string;
    }>;
  };
}

export interface CriticalIssue {
  doc_id: string;
  field: string;
  issue: string;
  severity: "high" | "medium" | "low";
  impact?: string;
  doc_type?: string;
}

export interface DocumentTypeBreakdown {
  doc_type: string;
  total_documents: number;
  documents_passed: number;
  documents_failed: number;
  quality_score: string;
  fields_validated: string[];
  field_wise_analysis: Record<string, FieldWiseAnalysis>;
  critical_issues: CriticalIssue[];
  recommendations: string[];
}

export interface DocumentWiseDetail {
  doc_id: string;
  doc_type: string;
  overall_status: "pass" | "fail";
  overall_summary: string;
  quality_score: string;
  fields_validated: number;
  fields_passed: number;
  fields_failed: number;
  issues_found: Array<{
    field: string;
    validation_type: string;
    status: "pass" | "fail";
    reason: string;
  }>;
  recommendations: string[];
}

export interface DateValidationDetail {
  field_pair: string;
  documents_checked: number;
  date_difference: string;
  pass_rate: string;
}

export interface BatchQualityResults {
  executive_summary: {
    summary: string;
    total_documents_checked: number;
    total_document_types: number;
    documents_passed: number;
    documents_failed: number;
    overall_quality_score: string;
    critical_issues_count: number;
    batch_id: string;
  };
  document_type_breakdown: DocumentTypeBreakdown[];
  document_wise_details: DocumentWiseDetail[];
  field_level_analysis: {
    date_validations: {
      total_date_fields: number;
      passed: number;
      failed: number;
      common_issues_with_reason: string[];
      details: DateValidationDetail[];
    };
    numeric_validations: {
      total_numeric_fields: number;
      passed: number;
      failed: number;
      common_issues: string[];
      statistics: {
        average_values: Record<string, number>;
        ranges: Record<string, [number, number]>;
      };
    };
    text_validations: {
      total_text_fields: number;
      null_fields_found: number;
      completeness_rate: string;
    };
  };
  critical_issues_summary: {
    high_priority: CriticalIssue[];
    medium_priority: CriticalIssue[];
    low_priority: CriticalIssue[];
  };
  recommendations: {
    document_type_specific: Record<string, string[]>;
    overall: string[];
  };
  quality_metrics: {
    documents_checked: number;
    quality_checks_passed: number;
    quality_checks_failed: number;
    overall_quality_score: string;
    pass_rate_by_doc_type: Record<string, string>;
  };
}
